import React from 'react';
import { X, Radar } from 'lucide-react';

export default function IntelligenceReportModal({ intelData, onClose }) {
  if (!intelData) return null;

  const { flavor_text, intel_reports } = intelData;

  // Country flag emojis
  const countryFlags = {
    'USA': '🇺🇸',
    'RU': '🇷🇺',
    'CN': '🇨🇳',
    'EU': '🇪🇺',
    'NK': '🇰🇵'
  };

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ 
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(4px)'
      }}
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{
          animation: 'scanlineAppear 0.3s ease-out'
        }}
      >
        {/* Classified Header */}
        <div 
          className="bg-gradient-to-r from-red-900 to-red-800 border-4 border-red-600 p-3 rounded-t-lg relative overflow-hidden"
          style={{
            boxShadow: '0 0 20px rgba(220, 38, 38, 0.6), inset 0 0 20px rgba(0,0,0,0.3)'
          }}
        >
          {/* Redacted bars effect */}
          <div className="absolute top-0 left-0 w-full h-1 bg-black opacity-60"></div>
          <div className="absolute bottom-0 left-0 w-full h-1 bg-black opacity-60"></div>
          
          <div className="flex items-center justify-between relative z-10">
            <div className="flex items-center gap-3">
              <Radar className="w-8 h-8 text-green-400 animate-pulse" />
              <div>
                <h2 
                  className="text-2xl font-bold uppercase tracking-widest"
                  style={{
                    fontFamily: "'Orbitron', monospace",
                    color: '#fbbf24',
                    textShadow: '0 0 10px rgba(251, 191, 36, 0.8), 0 2px 4px rgba(0,0,0,0.9)'
                  }}
                >
                  Classified Intelligence Report
                </h2>
                <p className="text-xs text-red-200 font-mono uppercase tracking-wider">
                  ■■■ TOP SECRET ■■■ EYES ONLY ■■■
                </p>
              </div>
            </div>
            
            <button
              onClick={onClose}
              className="p-2 hover:bg-red-700 rounded-lg transition-colors"
              title="Close Report"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div 
          className="bg-slate-900 border-4 border-green-600 p-6 rounded-b-lg"
          style={{
            boxShadow: '0 0 30px rgba(34, 197, 94, 0.5), inset 0 0 40px rgba(0,0,0,0.5)',
            background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)'
          }}
        >
          {/* CRT Scanline Effect */}
          <div 
            className="absolute inset-0 pointer-events-none opacity-10"
            style={{
              background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,0,0.1) 2px, rgba(0,255,0,0.1) 4px)',
              animation: 'scanline 8s linear infinite'
            }}
          />

          {/* Flavor Text */}
          <div 
            className="bg-black/50 border-2 border-green-500/30 rounded-lg p-4 mb-6 relative overflow-hidden"
            style={{
              boxShadow: 'inset 0 0 20px rgba(0,0,0,0.5)'
            }}
          >
            {/* Glitch effect overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-green-500/10 to-transparent animate-pulse"></div>
            
            <p 
              className="text-green-400 font-mono text-sm relative z-10"
              style={{
                textShadow: '0 0 5px rgba(34, 197, 94, 0.8)',
                letterSpacing: '0.05em'
              }}
            >
              📡 {flavor_text}
            </p>
          </div>

          {/* Intel Reports */}
          <div className="space-y-4">
            {intel_reports.map((report) => (
              <div 
                key={`${report.country_code}-${report.predicted_action}`}
                className="bg-slate-800/80 border-2 border-amber-500/40 rounded-lg p-4 hover:border-amber-500/70 transition-all"
                style={{
                  boxShadow: '0 0 15px rgba(245, 158, 11, 0.2), inset 0 2px 10px rgba(0,0,0,0.4)'
                }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="text-4xl">
                      {countryFlags[report.country_code] || '🏴'}
                    </div>
                    <div>
                      <h3 
                        className="text-xl font-bold text-amber-400"
                        style={{
                          fontFamily: "'Orbitron', monospace",
                          textShadow: '0 0 8px rgba(251, 191, 36, 0.6)'
                        }}
                      >
                        {report.country_name}
                      </h3>
                      <p className="text-xs text-slate-400 font-mono uppercase">
                        Code: {report.country_code}
                      </p>
                    </div>
                  </div>

                  {/* Confidence Badge */}
                  <div 
                    className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
                      report.confidence === 'HIGH' 
                        ? 'bg-green-600 text-white' 
                        : report.confidence === 'MEDIUM'
                        ? 'bg-yellow-600 text-white'
                        : 'bg-red-600 text-white'
                    }`}
                    style={{
                      boxShadow: '0 2px 8px rgba(0,0,0,0.4)'
                    }}
                  >
                    {report.confidence}
                  </div>
                </div>

                {/* Predicted Action */}
                <div className="bg-black/40 border border-green-500/30 rounded p-3">
                  <p className="text-xs text-green-300 font-mono uppercase mb-1">
                    ⚠️ Predicted Next Action:
                  </p>
                  <p 
                    className="text-lg font-bold text-red-400 font-mono"
                    style={{
                      textShadow: '0 0 10px rgba(248, 113, 113, 0.8)'
                    }}
                  >
                    {report.predicted_action}
                  </p>
                  {report.target_hint !== 'self' && report.target_hint !== 'random target' && (
                    <p className="text-sm text-slate-300 mt-1">
                      → Target: {report.target_hint}
                    </p>
                  )}
                  {report.target_hint === 'random target' && (
                    <p className="text-sm text-slate-400 mt-1 italic">
                      → Target: Unknown
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Disclaimer */}
          <div 
            className="mt-6 bg-yellow-900/20 border border-yellow-600/40 rounded p-3"
          >
            <p className="text-xs text-yellow-200 font-mono text-center">
              ⚠️ Intelligence estimates are not guaranteed. Enemy plans may change.
            </p>
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="w-full mt-4 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white font-bold py-3 px-6 rounded-lg uppercase tracking-wider transition-all"
            style={{
              fontFamily: "'Orbitron', monospace",
              boxShadow: '0 4px 12px rgba(220, 38, 38, 0.4)'
            }}
          >
            Acknowledge & Close
          </button>
        </div>
      </div>

      <style jsx>{`
        @keyframes scanlineAppear {
          0% { 
            opacity: 0; 
            transform: translateY(-20px) scale(0.95); 
          }
          100% { 
            opacity: 1; 
            transform: translateY(0) scale(1); 
          }
        }

        @keyframes scanline {
          0% { transform: translateY(0); }
          100% { transform: translateY(100%); }
        }

        @keyframes intelAppear {
          0% { 
            opacity: 0; 
            transform: translateX(-20px); 
          }
          100% { 
            opacity: 1; 
            transform: translateX(0); 
          }
        }
      `}</style>
    </div>
  );
}
